import React from 'react'
import { Bell, BellOff, TestTube } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { usePushNotifications } from '@/hooks/usePushNotifications'

export const PushNotificationManager = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications()

  const [swStatus, setSwStatus] = React.useState<string>('verificando...')
  const [subscriptionEndpoint, setSubscriptionEndpoint] = React.useState<string>('')

  React.useEffect(() => {
    const checkSW = async () => {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration?.active) {
          setSwStatus(registration.active.state)
          
          const subscription = await (registration as any).pushManager.getSubscription()
          if (subscription) {
            const endpoint = subscription.endpoint
            setSubscriptionEndpoint(`...${endpoint.slice(-30)}`)
          }
        } else {
          setSwStatus('não registrado')
        }
      } else {
        setSwStatus('não suportado')
      }
    }
    
    checkSW()
  }, [isSubscribed])

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Notificações Push
          </CardTitle>
          <CardDescription>
            Push notifications não são suportadas neste dispositivo ou navegador.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const getPermissionBadge = () => {
    switch (permission) {
      case 'granted':
        return <Badge variant="default" className="bg-green-500">Permitido</Badge>
      case 'denied':
        return <Badge variant="destructive">Negado</Badge>
      default:
        return <Badge variant="secondary">Não solicitado</Badge>
    }
  }

  const getSubscriptionBadge = () => {
    if (isSubscribed) {
      return <Badge variant="default" className="bg-blue-500">Ativo</Badge>
    }
    return <Badge variant="outline">Inativo</Badge>
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notificações Push
        </CardTitle>
        <CardDescription>
          Receba notificações instantâneas no seu dispositivo sobre atualizações importantes do condomínio.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm">Status da Permissão:</span>
          {getPermissionBadge()}
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm">Status da Inscrição:</span>
          {getSubscriptionBadge()}
        </div>

        <div className="flex flex-col gap-2">
          {!isSubscribed ? (
            <Button 
              onClick={subscribe} 
              disabled={isLoading}
              className="w-full"
            >
              <Bell className="h-4 w-4 mr-2" />
              {isLoading ? 'Ativando...' : 'Ativar Notificações'}
            </Button>
          ) : (
            <Button 
              onClick={unsubscribe} 
              disabled={isLoading}
              variant="outline"
              className="w-full"
            >
              <BellOff className="h-4 w-4 mr-2" />
              {isLoading ? 'Desativando...' : 'Desativar Notificações'}
            </Button>
          )}

          {isSubscribed && (
            <Button 
              onClick={sendTestNotification}
              variant="secondary"
              size="sm"
              className="w-full"
            >
              <TestTube className="h-4 w-4 mr-2" />
              Testar Notificação
            </Button>
          )}
        </div>

        {isSubscribed && (
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md space-y-1">
            <p><strong>Service Worker:</strong> {swStatus}</p>
            {subscriptionEndpoint && (
              <p><strong>Endpoint:</strong> {subscriptionEndpoint}</p>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground mt-4 space-y-1">
          <p>
            • As notificações funcionam mesmo quando o app está fechado
          </p>
          <p>
            • Você pode gerenciar permissões nas configurações do navegador
          </p>
          <p>
            • Notificações incluem mensagens, avisos e atualizações do sistema
          </p>
        </div>
      </CardContent>
    </Card>
  )
}